import { FlexAligner, Typography } from '@scaleaq/scaleui';
import { AspectRatio } from '@scaleaq/sitecontrol-lib';
import styled, { css } from 'styled-components';

import { StyledCardWrapperWithState } from '@/components/card/styledCardWrapperWithState';
import { Z_INDEX } from '@/constants';
import { colors, responsive } from '@/utils';

import {
  controlsBottomInset,
  controlsFadeTransition,
  controlsRightInset,
} from './cameraStreamControls.tokens.ts';
import { RadialControlsHost } from './cameraStreamWidgetWrapper/cameraRadialControls/cameraRadialControls.style.ts';
import { StyledCameraSwitchPanel } from './cameraSwitchPanel/cameraSwitchPanel.styles.ts';

export const borderRadius = `${responsive.radius.none}`;

/** Single-row feed footer: vertical padding + badge line height. */
const feedStatusFooterStackHeight = `calc(${responsive.spacing.xlarge} * 2 + ${responsive.spacing['2xlarge']})`;

export const StyledVideo = styled.video<{ $visible?: boolean }>`
  border-radius: ${borderRadius};
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  background: ${colors.palette.neutral.dark[700]};
  opacity: ${({ $visible = true }) => ($visible ? 1 : 0)};
  pointer-events: ${({ $visible = true }) => ($visible ? 'auto' : 'none')};

  ${({ $visible = true }) =>
    !$visible &&
    css`
      position: absolute;
      inset: 0;
    `}
`;

/** Always-visible pen name — lives inside the header but never hides. */
export const StyledPenName = styled(Typography)`
  color: ${colors.text.neutral.on.solid[100]};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  /* Flex items default to min-width: auto, which floors them at their text width; without
     this the name pushes the sensor readings out instead of collapsing to an ellipsis. */
  min-width: 0;
`;

/**
 * Header that does NOT use opacity to hide/show.
 * Background and children (except pen name) fade individually.
 */
export const StyledVideoHeader = styled(FlexAligner)<{ $zIndex: number }>`
  position: absolute;
  padding: ${responsive.spacing.medium};
  border-radius: ${borderRadius} ${borderRadius} 0 0;
  z-index: ${({ $zIndex }) => $zIndex};
  background-color: transparent;
  transition: background-color ${controlsFadeTransition};
`;

/** Wrapper for header controls that fade in on hover (when banner is unpinned). */
export const StyledHeaderControls = styled.div`
  display: flex;
  align-items: center;
  gap: ${responsive.spacing.small};
  /* Sensor readings, status and the menu keep their width; the name ellipsises instead. */
  flex-shrink: 0;
  opacity: 0;
  transition: opacity ${controlsFadeTransition};
  margin-left: auto;
`;

export const StyledVideoFooter = styled(FlexAligner)<{ $zIndex: number; $isFeeding: boolean }>`
  padding: ${responsive.spacing.medium};
  min-height: ${feedStatusFooterStackHeight};
  box-sizing: border-box;
  border-radius: 0 0 ${borderRadius} ${borderRadius};
  z-index: ${({ $zIndex }) => $zIndex};
  transition:
    opacity ${controlsFadeTransition},
    background-color ${controlsFadeTransition};
  background-color: ${colors.background.transparent[400]};
  opacity: ${({ $isFeeding }) => ($isFeeding ? 1 : 0)};
  pointer-events: ${({ $isFeeding }) => ($isFeeding ? 'auto' : 'none')};
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
`;

/** Bottom-right pause/resume; auto-hides on pointer idle unless the stream is paused (see useFrameRadialControlsVisibility). */
export const StyledPauseToggleHost = styled.div<{ $zIndex: number; $visible: boolean }>`
  position: absolute;
  right: ${controlsRightInset};
  bottom: ${controlsBottomInset};
  z-index: ${({ $zIndex }) => $zIndex};
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transition: opacity ${controlsFadeTransition};
  pointer-events: ${({ $visible }) => ($visible ? 'auto' : 'none')};
`;

/** Height reserved for the DVR bar; used to lift bottom-anchored controls clear of it. */
const dvrBarHeight = '1.50rem';

/** Bottom recall (DVR) scrubber bar; only rendered while rewinding (entered via the
    View Recent History button), so the slider is never visible until then. */
export const StyledDvrBar = styled.div<{ $zIndex: number; $visible: boolean }>`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  min-height: ${dvrBarHeight};
  display: flex;
  align-items: center;
  gap: ${responsive.spacing.medium};
  padding: ${responsive.spacing.small} ${responsive.spacing.large};
  box-sizing: border-box;
  z-index: ${({ $zIndex }) => $zIndex};
  background: linear-gradient(to top, ${colors.background.transparent[400]}, transparent);
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transition: opacity ${controlsFadeTransition};
  pointer-events: auto;
`;

/** Footprint of a scaleui IconButton size='large' (absoluteSize(2.25) in IconButton.js). */
const largeIconButtonSize = '2.25rem';
const dvrEntryAboveFeedFooterBottom = `calc(${controlsBottomInset} + ${largeIconButtonSize} + ${responsive.spacing.small} + ${feedStatusFooterStackHeight})`;

/** Compact host for the sole rewind entry point (View Recent History), shown while live.
    Stacked directly ABOVE the pause toggle in the bottom-right corner (same right offset),
    clear of the bottom-left PTZ pad and the bottom-centre feed-status footer. The scrubber
    bar stays unrendered until this button enters rewind, so it never clashes with other
    controls sharing the bottom strip. Auto-hides on pointer idle / non-hover, like the
    pause toggle and radial (PTZ) pad (see useFrameRadialControlsVisibility). */
export const StyledDvrEntryButtonHost = styled.div<{ $zIndex: number; $visible: boolean }>`
  position: absolute;
  right: ${controlsRightInset};
  bottom: calc(${controlsBottomInset} + ${largeIconButtonSize} + ${responsive.spacing.small});
  z-index: ${({ $zIndex }) => $zIndex};
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transition: opacity ${controlsFadeTransition};
  pointer-events: ${({ $visible }) => ($visible ? 'auto' : 'none')};
`;

/** Single-handle timeline scrubber across the recall window (left = oldest, right = live). */
export const StyledDvrScrubber = styled.input`
  flex: 1;
  min-width: 0;
  height: 4px;
  appearance: none;
  -webkit-appearance: none;
  border-radius: 999px;
  background: ${colors.background.transparent[200]};
  cursor: pointer;
  accent-color: ${colors.icon.action[100]};

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: #fff;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.6);
  }
  &::-moz-range-thumb {
    width: 14px;
    height: 14px;
    border: none;
    border-radius: 50%;
    background: #fff;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.6);
  }
`;

/** Offset readout ("LIVE" / "-12s"); tabular figures so it doesn't jitter while playing. */
export const StyledDvrLabel = styled.span`
  color: white;
  font-size: 0.8rem;
  font-variant-numeric: tabular-nums;
  min-width: 3.5rem;
  text-align: center;
  white-space: nowrap;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.8);
`;

const cameraControlsAboveFeedFooter = css`
  &:has(${StyledVideoFooter}) ${StyledPauseToggleHost} {
    bottom: calc(${controlsBottomInset} + ${feedStatusFooterStackHeight});
  }

  /* Rewind entry rides up with the pause toggle when the feed-status footer is shown,
     keeping the bottom-right stack (rewind above pause) intact. */
  &:has(${StyledVideoFooter}) ${StyledDvrEntryButtonHost} {
    bottom: ${dvrEntryAboveFeedFooterBottom};
  }

  &:has(${StyledVideoFooter}) ${RadialControlsHost} {
    bottom: ${feedStatusFooterStackHeight};
  }

  /* Lift bottom-anchored controls above the DVR bar so their hit-zones don't overlap it. */
  &:has(${StyledDvrBar}) ${StyledPauseToggleHost} {
    bottom: calc(${controlsBottomInset} + ${dvrBarHeight});
  }

  &:has(${StyledDvrBar}) ${RadialControlsHost} {
    bottom: ${dvrBarHeight};
  }

  /* DVR bar + feed footer together: stack the lift so controls clear both. */
  &:has(${StyledVideoFooter}):has(${StyledDvrBar}) ${StyledPauseToggleHost} {
    bottom: calc(${controlsBottomInset} + ${feedStatusFooterStackHeight} + ${dvrBarHeight});
  }

  &:has(${StyledVideoFooter}):has(${StyledDvrBar}) ${RadialControlsHost} {
    bottom: calc(${feedStatusFooterStackHeight} + ${dvrBarHeight});
  }

  &:has(${StyledVideoFooter}) ${StyledDvrBar} {
    bottom: ${feedStatusFooterStackHeight};
  }
`;

const revealVideoHeaderOnHoverOrFocus = css`
  &:hover ${StyledVideoHeader} {
    background-color: ${colors.background.transparent[400]};
  }
  &:hover ${StyledHeaderControls} {
    opacity: 1;
  }
  &:hover ${StyledVideoFooter} {
    opacity: 1;
    pointer-events: auto;
  }
  &:focus-within ${StyledVideoHeader} {
    background-color: ${colors.background.transparent[400]};
  }
  &:focus-within ${StyledHeaderControls} {
    opacity: 1;
  }
  &:focus-within ${StyledVideoFooter} {
    opacity: 1;
    pointer-events: auto;
  }
`;

/**
 * Camera group switch panel: revealed on hover or keyboard focus, independent of the banner pin.
 * Mouse click leaves `:focus` without `:focus-visible`, so the panel can hide when the pointer leaves.
 */
const revealCameraSwitchPanelOnHoverOrFocus = css`
  &:hover ${StyledCameraSwitchPanel}, &:has(:focus-visible) ${StyledCameraSwitchPanel} {
    opacity: 1;
    pointer-events: auto;
  }
`;

export const CustomStyledCardWrapperWithState = styled(StyledCardWrapperWithState)<{
  $aspectRatio: AspectRatio;
  $topBannerPinned?: boolean;
}>`
  aspect-ratio: ${({ $aspectRatio }) =>
    $aspectRatio === AspectRatio.Standard
      ? '4 / 3'
      : $aspectRatio === AspectRatio.Wide
        ? '16 / 9'
        : '1 / 1'};
  position: relative;
  transition: all ${controlsFadeTransition};

  ${cameraControlsAboveFeedFooter}

  ${({ $topBannerPinned }) =>
    $topBannerPinned
      ? css`
          ${StyledVideoHeader} {
            background-color: ${colors.background.transparent[400]};
          }
          ${StyledHeaderControls} {
            opacity: 1;
          }
        `
      : revealVideoHeaderOnHoverOrFocus}

  ${revealCameraSwitchPanelOnHoverOrFocus}

  &:fullscreen {
    aspect-ratio: unset;
    border-radius: 0;

    ${StyledVideo} {
      border-radius: 0;
    }

    ${StyledVideoHeader} {
      border-radius: 0;
    }

    ${StyledPenName} {
      font-size: 1.8rem;
    }
  }

  /* Focus border uses a pseudo-element overlay so it renders on top of the
     video without causing layout shift or being obscured by the <video> element. */
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    border: 0.3rem solid transparent;
    pointer-events: none;
    z-index: 1;
  }

  &:focus {
    outline: none;
  }

  /* Include overlay controls that hold DOM focus after click (pointerdown focuses this card). */
  &:focus-within::after {
    border-color: ${colors.stroke.info.solid[200]};
    border-width: 5px;
    z-index: ${Z_INDEX.overlay.loading + 3};
  }
`;
